import { CSSProperties, useMemo } from 'react'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import EventNoteIcon from '@mui/icons-material/EventNote'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import capitalize from 'lodash/capitalize'

import { useListLocationsQuery } from '../../../services/options'
import { formatHumanDateTime } from '../../../utils'

const Subtitle = styled('span')<{ primary: boolean }>(({ theme, primary }) => ({
  color: primary ? theme.colors.accent[500] : theme.palette.text.secondary,
  lineHeight: 1.4,
  fontWeight: 500,
  fontSize: '0.9rem',
}))

export interface EventInfoBoxProps {
  start: string | undefined
  end: string | undefined
  location: number | undefined | null
  margin?: CSSProperties['margin']
  direction?: 'column' | 'column-reverse' | 'row' | 'row-reverse'
  primary?: boolean
}

export default function EventInfoBox({
  start,
  end,
  location: rawLocation,
  margin,
  direction = 'row',
  primary = true,
}: EventInfoBoxProps) {
  const { data: locationsData } = useListLocationsQuery()

  const location = useMemo(
    () => (rawLocation ? locationsData?.find(elem => elem.id === rawLocation) : undefined),
    [rawLocation, locationsData]
  )

  return (
    <Stack
      direction={{ xs: 'column', sm: direction }}
      alignItems={{ xs: 'start', sm: direction === 'row' ? 'center' : 'start' }}
      gap="1rem"
      padding="1rem"
      flex="1 1 auto"
      margin={margin}
      sx={theme => ({
        backgroundColor: theme.colors.greyBackground,
        borderRadius: 4,
        width: '100%',
      })}
    >
      <Stack direction="row" alignItems="center" gap="1rem" flex="1 0">
        <EventNoteIcon
          sx={theme => ({
            fill: primary ? theme.colors.accent[500] : theme.palette.text.secondary,
          })}
        />
        <Stack alignItems="start">
          <Subtitle primary={primary}>{capitalize(formatHumanDateTime(start))}</Subtitle>
          <Subtitle primary={primary}>{capitalize(formatHumanDateTime(end))}</Subtitle>
        </Stack>
      </Stack>
      {location ? (
        <Stack direction="row" alignItems="center" gap="1rem" flex="1 0">
          <LocationOnIcon
            sx={theme => ({
              fill: primary ? theme.colors.accent[500] : theme.palette.text.secondary,
            })}
          />
          <Stack alignItems="start">
            <Subtitle primary={primary}>{location?.name}</Subtitle>
            {location?.address1 && <Subtitle primary={primary}>{location.address1}</Subtitle>}
            {location?.address2 && <Subtitle primary={primary}>{location.address2}</Subtitle>}
            <Subtitle primary={primary}>
              {location?.city} {location?.postcode}
            </Subtitle>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  )
}
